// 에너지 장치(스마트폴 · 태양광큐브) 응답을 화면이 쓰는 한 가지 모양으로 맞춘다.
//
// 1차 범위에서는 BE 가 아직 옛 모양(poleInfo 1개, overview.device + energy.latest/daily)을 내려준다.
// 새 모양(info.energyDevices / overview.energy.devices)이 오면 그대로 쓰고, 옛 모양이면 장치
// 1대짜리 목록으로 바꾼다. 화면 코드는 새 모양만 알면 된다. BE 가 바뀌면 옛 분기만 지우면 된다.
//
// 모양은 다중큐브_설계노트 6장과 같다.
//   info     : { mode, devices: [{ id, name, serialNum, deviceType, role, latitude, longitude,
//                                  operatingStatus, batteryCharge, isConnected, lastSeenAt }] }
//   overview : { mode, devices: [{ deviceSn, deviceType, role, name, operatingStatus, isConnected,
//                                  latest, forecast[], daily[] }], group }
//   mode     : POLE | EDGE_ONLY | FOG | NONE

export const ENERGY_MODES = ["POLE", "EDGE_ONLY", "FOG", "NONE"];

// 장치 목록으로 구성을 판단한다. BE 가 mode 를 내려주면 그 값을 쓰고, 없을 때만 쓴다.
export const energyModeOf = (devices = []) => {
  if (!devices.length) return "NONE";
  if (devices.some((device) => device.deviceType === "POLE")) return "POLE";
  const hasFog = devices.some((device) => device.role === "FOG");
  const hasEdge = devices.some((device) => device.role !== "FOG");
  // fog 혼자 남은 농장은 공유할 상대가 없으니 엣지만 구성으로 본다(설계노트 12장 5번).
  return hasFog && hasEdge ? "FOG" : "EDGE_ONLY";
};

const normalizeMode = (mode, devices) =>
  ENERGY_MODES.includes(mode) ? mode : energyModeOf(devices);

// fog 를 맨 위로. 나머지는 받은 순서(BE 가 id 순으로 내려준다).
export const sortFogFirst = (devices = []) =>
  [...devices].sort((a, b) => Number(b.role === "FOG") - Number(a.role === "FOG"));

export const findFog = (devices = []) => devices.find((device) => device.role === "FOG") ?? null;

// 장치가 연결돼 있나. isConnected 가 없으면(heartbeat 가 아직 없는 장치) 모른다 → null.
export const deviceOnline = (device) =>
  device?.isConnected == null ? null : Boolean(device.isConnected);

// GET /dashboard/{id}/info → { mode, devices }
export const normalizeInfoEnergyDevices = (info) => {
  const next = info?.energyDevices;
  if (next && Array.isArray(next.devices)) {
    return { mode: normalizeMode(next.mode, next.devices), devices: next.devices };
  }

  const pole = info?.poleInfo;
  if (!pole) return { mode: "NONE", devices: [] };
  const deviceType = pole.deviceType === "CUBE" ? "CUBE" : "POLE";
  return {
    mode: deviceType === "CUBE" ? "EDGE_ONLY" : "POLE",
    devices: [
      {
        id: pole.id ?? pole.serialNum ?? pole.deviceSn ?? "pole",
        name: pole.name,
        serialNum: pole.serialNum ?? pole.deviceSn ?? null,
        deviceType,
        role: deviceType === "CUBE" ? "EDGE" : null,
        latitude: pole.latitude ?? null,
        longitude: pole.longitude ?? null,
        operatingStatus: pole.operatingStatus ?? null,
        batteryCharge: pole.batteryCharge ?? null,
        isConnected: pole.isConnected ?? null,
        lastSeenAt: pole.lastSeenAt ?? null,
      },
    ],
  };
};

// GET /dashboard/{id}/overview → { mode, devices, group }
export const normalizeOverviewEnergy = (payload) => {
  const energy = payload?.energy;
  if (energy && Array.isArray(energy.devices)) {
    return {
      mode: normalizeMode(energy.mode, energy.devices),
      devices: energy.devices,
      group: energy.group ?? null,
    };
  }

  const device = payload?.device;
  if (!device?.deviceSn) return { mode: "NONE", devices: [], group: null };
  const deviceType = device.deviceType === "CUBE" ? "CUBE" : "POLE";
  return {
    mode: deviceType === "CUBE" ? "EDGE_ONLY" : "POLE",
    devices: [
      {
        deviceSn: device.deviceSn,
        deviceType,
        role: deviceType === "CUBE" ? "EDGE" : null,
        name: device.name ?? null,
        operatingStatus: device.operatingStatus ?? null,
        isConnected: device.isConnected ?? null,
        latest: energy?.latest ?? null,
        forecast: [],
        daily: Array.isArray(energy?.daily) ? energy.daily : [],
      },
    ],
    group: null,
  };
};

export const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// 실시간 발전 = 태양광 + 풍력. 큐브는 풍력 발전기가 없어 값이 와도 버린다(PowerGeneration 과 같은 규칙).
export const latestGenW = (latest, deviceType) => {
  if (!latest) return null;
  const solar = toNumberOrNull(latest.currentSolarW) ?? 0;
  const wind = deviceType === "CUBE" ? 0 : toNumberOrNull(latest.currentWindW) ?? 0;
  return solar + wind;
};

// "2분 전" 처럼 수신 경과를 보여준다. 값이 낡았는지 화면에서 바로 보이게 하려는 것.
export const formatAgo = (isoString, t, now = Date.now()) => {
  if (!isoString) return null;
  const parsed = new Date(isoString);
  if (Number.isNaN(parsed.getTime())) return null;

  const minutes = Math.floor((now - parsed.getTime()) / 60000);
  if (minutes < 1) return t("energy.ago.justNow");
  if (minutes < 60) return t("energy.ago.minutes", { n: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("energy.ago.hours", { n: hours });
  return t("energy.ago.days", { n: Math.floor(hours / 24) });
};

// 지도 마커 툴팁. 문구는 i18n 이 갖고 값만 여기서 채운다. 실계정 · 데모 공통.
// fog 구성에서만 역할을 붙인다 — 엣지만 구성에서 역할 배지를 붙이면 연결할 상대도 없는데 헷갈린다.
export const energyDeviceTooltip = (device, t, { mode } = {}) => {
  const online = deviceOnline(device);
  const showRole = mode === "FOG" && device.role;
  const head = showRole ? `${device.name} · ${t(`map.device.role.${device.role}`)}` : device.name;

  let status;
  if (online === false) status = t("map.device.disconnected");
  else if (device.operatingStatus == null && online == null) status = t("map.device.unknown");
  else status = t(device.operatingStatus ? "map.device.operating" : "map.device.abnormal");

  const battery = toNumberOrNull(device.batteryCharge);
  const batteryText =
    online !== false && battery != null
      ? ` · ${t("map.device.battery", { battery: Math.round(battery) })}`
      : "";
  return `${head}\n${status}${batteryText}`;
};
